import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  AssetTrackingFunc,
  BaseTrackingFuncProps as BaseTrackingFuncParams,
  useEnvironment,
} from "../Environment";
import { sdtvError } from "./common/SdtvPlayerError";
import { useConferenceEnvironment } from "./ConferenceEnvironment";

// contains asset specific stuff e.g. needed for tracking
interface AssetEnvironmentState {
  trackAsset: AssetTrackingFunc;
  trackAssetWithAds: AssetTrackingFunc;
  setMonetization: Dispatch<SetStateAction<string>>;
  conferenceIndex?: number;
  assetId: string;
}

const AssetEnvironmentContext = React.createContext<
  AssetEnvironmentState | undefined
>(undefined);

export interface AssetEnvironmentProps {
  conferenceIndex?: number;
  assetId: string;
}

export function AssetEnvironment({
  children,
  conferenceIndex,
  assetId,
}: PropsWithChildren<AssetEnvironmentProps>) {
  const conferenceEnv = useConferenceEnvironment();
  const zone = useMemo(() => {
    if (conferenceEnv?.conferenceState.bigIndex === undefined) {
      return "default";
    }
    if (conferenceEnv.conferenceState.bigIndex === conferenceIndex) {
      return "conference_big";
    } else return "conference_small";
  }, [conferenceEnv?.conferenceState.bigIndex, conferenceIndex]);
  const trackingFunc = useEnvironment().trackingFunc;
  const [adWasPlayed, setAdWasPlayed] = useState(false);
  const [monetization, setMonetization] = useState("");
  const trackAsset = useCallback(
    (...baseParams: BaseTrackingFuncParams) => {
      trackingFunc(
        baseParams[0],
        baseParams[1],
        baseParams[2],
        zone,
        adWasPlayed,
        monetization ? monetization : undefined
      );
    },
    [trackingFunc, zone, adWasPlayed, monetization]
  );
  const trackAssetWithAds = useCallback(
    (...baseParams: BaseTrackingFuncParams) => {
      trackingFunc(
        baseParams[0],
        baseParams[1],
        baseParams[2],
        zone,
        true,
        monetization ? monetization : undefined
      );
      setAdWasPlayed(true);
    },
    [trackingFunc, zone, monetization]
  );

  return (
    <AssetEnvironmentContext.Provider
      value={{
        trackAsset,
        trackAssetWithAds,
        setMonetization,
        conferenceIndex,
        assetId,
      }}
    >
      {children}
    </AssetEnvironmentContext.Provider>
  );
}

export function useAssetEnvironment() {
  const env = useContext(AssetEnvironmentContext);
  if (env) {
    return env;
  } else {
    throw sdtvError("AssetEnvironment not initialized", "default");
  }
}

export function useAssetEnvironmentSafe() {
  const env = useContext(AssetEnvironmentContext);
  if (env) {
    return env;
  }
}
